/*
 * Copyright (c) 2024. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type Point from '@mapbox/point-geometry';
import { type BuildableConstraint, format, type ParcelLayerProperties } from '@poweralpha/common';
import { DomUtil, type Layer, type Map as LeafletMap } from 'leaflet';
import type { Feature } from 'protomaps-leaflet';
import type { Dispatch, SetStateAction } from 'react';
import type { LayerStyleBuilder, TooltipBuilder } from './types';

type RGB = [number, number, number];

export const CONSTRAINT_RGB: Record<string, RGB> = {
  Slope: [18, 18, 16],
  Roads: [246, 207, 101],
  Wetlands: [91, 166, 85],
  'Water Features': [48, 190, 255],
  'Wind Turbines': [255, 48, 93],
  'Electric Transmission': [211, 211, 211],
  'Flood Risk': [162, 48, 255],
  Structures: [183, 129, 74],
  default: [0, 0, 255],
};

class ConstraintsSymbolizer {
  draw(context: CanvasRenderingContext2D, geom: Point[][], _z: number, feature: Feature): void {
    const key = typeof feature?.props?.name === 'string' ? feature.props.name : 'default';
    const rgb = CONSTRAINT_RGB[key] ?? CONSTRAINT_RGB.default;
    const [r, g, b] = rgb;

    // Build path for all rings
    context.beginPath();
    for (const ring of geom) {
      if (!ring.length) continue;
      context.moveTo(ring[0].x, ring[0].y);
      for (let i = 1; i < ring.length; i++) {
        const pt = ring[i];
        context.lineTo(pt.x, pt.y);
      }
      context.closePath();
    }

    // Fill: same color, 0.2 opacity
    context.fillStyle = `rgb(${r} ${g} ${b} / 20%)`;
    context.fill();

    // Stroke: same color, 1px, full opacity
    context.setLineDash([4, 4]);
    context.lineWidth = 1;
    context.strokeStyle = `rgb(${r} ${g} ${b} / 100%)`;
    context.stroke();

    // Reset line dash so it doesn't affect subsequent drawing
    context.setLineDash([]);
  }
}

// Symbology
export const constraintsPaintRules = [
  {
    dataLayer: 'constraints',
    symbolizer: new ConstraintsSymbolizer(),
  },
];

export const normalizeSiteId = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return normalized || undefined;
};

export const getLayerStyle: LayerStyleBuilder = (parcel, selected) => {
  let fillColor: string;
  let color = '#65FAFD';
  let fillOpacity: number | undefined;
  switch (parcel.BN_Agreement_Status) {
    case 'NA':
      fillColor = 'lightgrey';
      fillOpacity = 0;
      break;
    case 'Targeted':
      fillColor = '#73B2FF';
      break;
    case 'Landowner Negotiating':
      fillColor = '#FFFFAF';
      break;
    case 'Effective':
      fillColor = '#38A800';
      break;
    case 'Landowner Committed':
      fillColor = '#FFFF73';
      break;
    case 'Unknown':
      fillColor = '#FF00C5';
      break;
    case 'Landowner Contacted':
      fillColor = '#C5C5C5';
      break;
    case 'Landowner Refused':
      fillColor = '#F37B76';
      break;
    case 'Landowner Unresponsive':
      fillColor = '#5D5D5D';
      break;
    case 'Landowner Signed':
      fillColor = '#A7FE72';
      break;
    case 'Expired':
      fillColor = '#D52916';
      break;
    case 'Other':
      fillColor = '#F26ED8';
      break;
    case 'Undetermined':
      fillColor = 'white';
      fillOpacity = 0;
      break;
    case 'Terminated':
      fillColor = 'black';
      break;
    default:
      fillColor = '#F028BB';
  }

  if (selected?.(parcel)) {
    return { fillColor, color, fillOpacity: fillOpacity ?? 0.5, weight: 3 };
  }

  switch (parcel.BN_Agreement_Type) {
    case 'Lease':
      color = '#6EFD31';
      break;
    case 'Purchase':
      color = '#235EFA';
      break;
    case 'Easement':
      color = '#F6A631';
      break;
    case 'Other':
      color = '#F26ED8';
      break;
    case null:
      color = '#C5C5C5';
      break;
    default:
      color = '#F028BB';
  }

  return { fillColor, color, fillOpacity: fillOpacity ?? 0.5, weight: 1 };
};

export const projectParcelsTooltip: TooltipBuilder = (layer) => {
  const parcel = (layer as unknown as ParcelLayerProperties).feature.properties;
  return `
    <table>
      <tbody>
        <tr>
          <td>Owner:</td>
          <td>${
            (parcel.Owner_Name?.length ?? 0) > 15
              ? `${parcel.Owner_Name?.slice(0, 15)}...`
              : (parcel.Owner_Name ?? 'unknown')
          }</td>
        </tr>
        <tr>
          <td>Parcel ID:</td>
          <td>${parcel.Source_Parcel_ID}</td>
        </tr>
        <tr>
          <td>Site:</td>
          <td>${parcel.BN_CRM_Site_ID}</td>
        </tr>
        <tr>
          <td>Buildability:</td>
          <td>${format(parcel.Parcel_Buildable_Pct, 2)}%</td>
        </tr>
          <td>Agreement Status:</td>
          <td>${parcel.BN_Agreement_Status}</td>
        </tr>
        <tr>
          <td>Agreement Type:</td>
          <td>${parcel.BN_Agreement_Type}</td>
        </tr>
        <tr>
          <td>Project:</td>
          <td>${parcel.BN_Project}</td>
        </tr>
      </tbody>
    </table>
  `;
};
export const projectParcelsPopup: TooltipBuilder = (layer) => {
  const parcel = (layer as unknown as ParcelLayerProperties).feature.properties;
  return `
    <table>
      <tbody>
        <tr>
          <td>Owner:</td>
          <td>${
            (parcel.Owner_Name?.length ?? 0) > 15
              ? `${parcel.Owner_Name?.slice(0, 15)}...`
              : (parcel.Owner_Name ?? 'unknown')
          }</td>
        </tr>
        <tr>
          <td>Parcel ID:</td>
          <td>${parcel.Source_Parcel_ID}</td>
        </tr>
        <tr>
          <td>Site:</td>
          <td><a href=${parcel.BN_CRM_Site_Link}>${parcel.BN_CRM_Site_ID}</a></td>
        </tr>
        <tr>
          <td>Parcel Acreage:</td>
          <td>${format(parcel.Area_Ac, 1)} ac</td>
        </tr>
        <tr>
          <td>Parcel Buildable Area:</td>
          <td>${format(parcel.Parcel_Buildable_Ac, 2)} ac</td>
        </tr>
        <tr>
          <td>Parcel Buildability:</td>
          <td>${format(parcel.Parcel_Buildable_Pct, 2)} %</td>
        </tr>
        <tr>
          <td>Site Acreage:</td>
          <td>${format(parcel.Site_Ac, 1)} ac</td>
        </tr>
        <tr>
          <td>Site Buildable Area:</td>
          <td>${format(parcel.Site_Buildable_Ac, 2)} ac</td>
        </tr>
        <tr>
          <td>Site Buildability:</td>
          <td>${format(parcel.Site_Buildable_Pct, 2)} %</td>
        </tr>
        <tr>
          <td>Agreement Status:</td>
          <td>${parcel.BN_Agreement_Status}</td>
        </tr>
        <tr>
          <td>Agreement Type:</td>
          <td>${parcel.BN_Agreement_Type}</td>
        </tr>
        <tr>
          <td>County:</td>
          <td>${parcel.US_County}</td>
        </tr>
        <tr>
          <td>State:</td>
          <td>${parcel.US_State}</td>
        </tr>
        <tr>
          <td>Project:</td>
          <td>${parcel.BN_Project}</td>
        </tr>
      </tbody>
    </table>
  `;
};

export const buildableAreaConstraints: Array<BuildableConstraint['name']> = [
  'Slope',
  'Wetlands',
  'Roads',
  'Flood Risk',
  'Wind Turbines',
  'Water Features',
  'Electric Transmission',
  'Structures',
];

export const siteDefaultStyle = {
  fill: false,
  color: '#00202E',
  weight: 3,
  opacity: 1,
};
export const siteMouseoverStyle = {
  fill: false,
  color: '#00202E',
  weight: 5,
  opacity: 1,
};

function getConstraintsLegendHtml(constraintsVisible?: boolean) {
  // TODO: need to add the setback info back here, from the DB after implementing write to table???
  if (constraintsVisible === false || constraintsVisible === undefined) {
    return '';
  }
  const constraintRows = buildableAreaConstraints.map((constraint) => {
    const rgb = CONSTRAINT_RGB[constraint] ?? CONSTRAINT_RGB.default;
    const [r, g, b] = rgb;
    return {
      label:
        `<i class="circle" style="background:rgb(${r} ${g} ${b} / 20%);` +
        ` border-color:rgb(${r} ${g} ${b} / 100%);"></i> ${constraint}`,
    };
  });

  const legendTitle =
    '<h3 style="margin-bottom: 4px; margin-top: 4px;">Buildable Area Constraints</h3>';
  const labelsTable = constraintRows.map((row) => `<tr><td>${row.label}</td></tr>`);

  return `${legendTitle}<table>${labelsTable.join('')}</table>`;
}

export const getLegendHtml = (
  buildableConstraintsVisible?: boolean,
  visualEsrHtml?: string,
  addtlHtml?: string,
): string | undefined => {
  const nonBuildableConstraintsHtml = getConstraintsLegendHtml(buildableConstraintsVisible);
  const esrHtml = visualEsrHtml ? `<div style='margin-top: 12px;' >${visualEsrHtml}</div>` : '';
  const bottomHtml = addtlHtml ? `<div style='margin-top: 12px;' >${addtlHtml}</div>` : '';
  return `${nonBuildableConstraintsHtml}${esrHtml}${bottomHtml}`;
};

// Helper to set up the project parcel filter UI under the map layers control
export function setupProjectFilterControl(
  container: HTMLElement | undefined,
  map: LeafletMap,
  projectFilter: 'all' | 'nonExcluded',
  setProjectFilter: Dispatch<SetStateAction<'all' | 'nonExcluded'>>,
  hasProjectFeatures: boolean,
  projectParcelLayer: Layer | null,
) {
  const oldFilter = container?.querySelector('.project-filter-control');
  if (oldFilter) {
    oldFilter.remove();
  }
  const filterDiv = DomUtil.create('div', 'project-filter-control') as HTMLElement;
  const nonExcludedInfoPopup =
    'Parcels in bnLand that have values other than: ' +
    "a) Agreement status: 'Landowner Refused', 'Landowner Unresponsive', " +
    "'Expired', 'Terminated', or b) Agreement type: 'Easement'";

  filterDiv.innerHTML = `
    <form style="margin:0; padding:4px;">
      <strong style="display:block; margin-bottom:4px;">Project Parcel Filter:</strong>
      <label>
        <input
          type="radio"
          name="projectFilter"
          value="all"
          title="All parcels in bnLand that are tagged with the project name."
          ${projectFilter === 'all' ? 'checked' : ''}
        /> All parcels
      </label>
      <label>
        <input
          type="radio"
          name="projectFilter"
          value="nonExcluded"
          title="${nonExcludedInfoPopup}"
          ${projectFilter === 'nonExcluded' ? 'checked' : ''}
        /> Non-excluded parcels
      </label>
    </form>
  `;
  const allInput = filterDiv.querySelector("input[value='all']") as HTMLInputElement;
  const nonExcludedInput = filterDiv.querySelector(
    "input[value='nonExcluded']",
  ) as HTMLInputElement;
  allInput.addEventListener('change', () => setProjectFilter('all'));
  nonExcludedInput.addEventListener('change', () => setProjectFilter('nonExcluded'));
  const updateDisableState = () => {
    const visible =
      hasProjectFeatures && (projectParcelLayer ? map.hasLayer(projectParcelLayer) : false);
    allInput.disabled = !visible;
    nonExcludedInput.disabled = !visible;
    filterDiv.style.opacity = visible ? '1' : '0.5';
    filterDiv.style.pointerEvents = visible ? 'auto' : 'none';
  };
  updateDisableState();
  const onToggle = (e: { name: string }) => {
    if (e.name === 'Project Parcels') {
      updateDisableState();
    }
  };
  map.on('overlayadd', onToggle);
  map.on('overlayremove', onToggle);
  container?.appendChild(filterDiv);
  return () => {
    map.off('overlayadd', onToggle);
    map.off('overlayremove', onToggle);
    filterDiv.remove();
  };
}
